import type {
  AuditEntry,
  BanLog,
  Chat,
  ChatOptions,
  ChatAvatarSync,
  ChatSettingsPatch,
  ChatStats,
  ImageCacheContentFilter,
  ImageCacheEntry,
  ImageCacheLabelFilter,
  ImageCachePage,
  ListEntry,
  OverviewStats,
  Page,
  Pattern,
  PatternExport,
  PatternTest,
  SettingDefinition,
  SpamMessage,
  User,
  UserAction,
  UserDetail,
  UserSummary,
  VoteCast,
  VoteSession
} from "./types";

const apiRoot = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, "") || "/api";

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

function cookie(name: string): string | null {
  const prefix = `${encodeURIComponent(name)}=`;
  const item = document.cookie.split("; ").find((entry) => entry.startsWith(prefix));
  return item ? decodeURIComponent(item.slice(prefix.length)) : null;
}

function query(params: Record<string, string | number | null | undefined>): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") search.set(key, String(value));
  });
  const value = search.toString();
  return value ? `?${value}` : "";
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method || "GET").toUpperCase();
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    const csrf = cookie("cg_admin_csrf");
    if (csrf) headers.set("X-CSRF-Token", csrf);
  }
  const response = await fetch(`${apiRoot}${path}`, { ...init, headers, credentials: "include" });
  if (!response.ok) {
    let message = `Ошибка API (${response.status})`;
    try {
      const payload = (await response.json()) as { detail?: string | Array<{ msg?: string }> };
      if (typeof payload.detail === "string") message = payload.detail;
      else if (Array.isArray(payload.detail)) message = payload.detail.map((item) => item.msg).filter(Boolean).join(". ") || message;
    } catch {
      // Keep the status-based fallback.
    }
    if (response.status === 401 && !["/auth/challenge", "/auth/verify"].includes(path)) {
      window.dispatchEvent(new Event("cg-session-expired"));
    }
    throw new ApiError(response.status, message);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  startLogin: (login: string) => request<{ accepted: boolean; expires_in: number }>("/auth/challenge", { method: "POST", body: JSON.stringify({ login }) }),
  verifyLogin: (code: string) => request<{ success: boolean }>("/auth/verify", { method: "POST", body: JSON.stringify({ code }) }),
  me: (signal?: AbortSignal) => request<User>("/auth/me", { signal }),
  logout: () => request<void>("/auth/logout", { method: "POST" }),
  chats: (page = 1, q = "", signal?: AbortSignal) => request<Page<Chat>>(`/chats${query({ page, limit: 50, q })}`, { signal }),
  chatOptions: (q = "", signal?: AbortSignal) => request<ChatOptions>(`/chats/options${query({ q, limit: 100 })}`, { signal }),
  chat: (chatId: number, signal?: AbortSignal) => request<Chat>(`/chats/${chatId}`, { signal }),
  settingDefinitions: (signal?: AbortSignal) => request<SettingDefinition[]>("/chat-settings/schema", { signal }),
  updateSettings: (chatId: number, patch: ChatSettingsPatch) => request<Chat>(`/chats/${chatId}/settings`, { method: "PATCH", body: JSON.stringify(patch) }),
  reloadAdmins: (chatId: number) => request<Chat>(`/chats/${chatId}/reload-admins`, { method: "POST" }),
  syncChatAvatars: () => request<ChatAvatarSync>("/chats/sync-avatars", { method: "POST" }),
  stats: (chatId: number, signal?: AbortSignal) => request<ChatStats>(`/chats/${chatId}/stats`, { signal }),
  audit: (chatId: number, signal?: AbortSignal) => request<AuditEntry[]>(`/chats/${chatId}/audit`, { signal }),

  searchUsers: (q: string, signal?: AbortSignal) => request<UserSummary[]>(`/users/search${query({ q })}`, { signal }),
  user: (userId: number, signal?: AbortSignal) => request<UserDetail>(`/users/${userId}`, { signal }),
  banUser: (userId: number, chatId: number | null, reason: string) => request<UserAction>(`/users/${userId}/ban`, { method: "POST", body: JSON.stringify({ chat_id: chatId, reason }) }),
  unbanUser: (userId: number, chatId: number | null, reason: string) => request<UserAction>(`/users/${userId}/unban`, { method: "POST", body: JSON.stringify({ chat_id: chatId, reason }) }),
  approveUser: (userId: number, chatId: number | null, reason: string) => request<UserAction>(`/users/${userId}/approve`, { method: "POST", body: JSON.stringify({ chat_id: chatId, reason }) }),
  removeApproval: (userId: number, chatId: number | null, reason: string) => request<UserAction>(`/users/${userId}/approve${query({ chat_id: chatId, reason })}`, { method: "DELETE" }),

  userList: (kind: "spamers" | "approved" | "administrators", page: number, q = "", signal?: AbortSignal) => request<Page<ListEntry>>(`/admin/${kind}${query({ page, limit: 50, q })}`, { signal }),
  addUserList: (kind: "spamers" | "approved" | "administrators", userId: number, reason: string) => request<UserAction>(`/admin/${kind}`, { method: "POST", body: JSON.stringify({ user_id: userId, reason }) }),
  removeUserList: (kind: "spamers" | "approved" | "administrators", userId: number) => request<UserAction>(`/admin/${kind}/${userId}`, { method: "DELETE" }),

  patterns: (source: "blacklist" | "extra", q = "", signal?: AbortSignal) => request<Pattern[]>(`/patterns/${source}${query({ q, limit: 500 })}`, { signal }),
  addPattern: (source: "blacklist" | "extra", word: string, ban: boolean) => request<Pattern>(`/patterns/${source}`, { method: "POST", body: JSON.stringify({ word, ban }) }),
  deletePattern: (source: "blacklist" | "extra", id: number) => request<void>(`/patterns/${source}/${id}`, { method: "DELETE" }),
  testPatterns: (text: string, signal?: AbortSignal) => request<PatternTest>("/patterns/test", { method: "POST", body: JSON.stringify({ text }), signal }),
  reloadPatterns: () => request<void>("/patterns/reload", { method: "POST" }),
  exportPatterns: () => request<PatternExport>("/patterns/export"),
  importPatterns: (payload: { mode: "append" | "replace"; blacklist: Array<{ word: string; ban: boolean }>; extra: Array<{ word: string; ban: boolean }> }) => request<{ blacklist_count: number; extra_count: number; mode: string }>("/patterns/import", { method: "POST", body: JSON.stringify(payload) }),

  imageCache: (params: { page: number; q?: string; label: ImageCacheLabelFilter; content: ImageCacheContentFilter }, signal?: AbortSignal) => request<ImageCachePage>(`/image-cache${query({ ...params, limit: 24 })}`, { signal }),
  imageCacheImageUrl: (imageHash: string) => `${apiRoot}/image-cache/${encodeURIComponent(imageHash)}/image`,
  updateImageCacheLabel: (imageHash: string, isSpam: boolean) => request<ImageCacheEntry>(`/image-cache/${encodeURIComponent(imageHash)}`, { method: "PATCH", body: JSON.stringify({ is_spam: isSpam }) }),
  deleteImageCacheEntry: (imageHash: string) => request<void>(`/image-cache/${encodeURIComponent(imageHash)}`, { method: "DELETE" }),
  clearCleanImageCache: () => request<{ deleted: number }>("/image-cache", { method: "DELETE" }),

  banLogs: (params: Record<string, string | number | null | undefined>, signal?: AbortSignal) => request<Page<BanLog>>(`/logs/bans${query({ limit: 50, ...params })}`, { signal }),
  spamLogs: (params: Record<string, string | number | null | undefined>, signal?: AbortSignal) => request<Page<SpamMessage>>(`/logs/spam-messages${query({ limit: 50, ...params })}`, { signal }),
  auditLogs: (params: Record<string, string | number | null | undefined>, signal?: AbortSignal) => request<Page<AuditEntry>>(`/logs/audit${query({ limit: 50, ...params })}`, { signal }),
  overview: (signal?: AbortSignal) => request<OverviewStats>("/stats/overview", { signal }),

  activeVotes: (signal?: AbortSignal) => request<VoteSession[]>("/votes/active", { signal }),
  vote: (sessionId: number, vote: "for" | "against") => request<VoteCast>(`/votes/${sessionId}/vote`, { method: "POST", body: JSON.stringify({ vote }) })
};
